import FullscreenSpinner from '@/components/common/FullScreenSpinner';
import DnsCheckForm from '@/components/DnsCheckForm';
import DnsResults from '@/components/DnsResults';
import { LogoIntegrityTag } from '@/components/LogoIntegrityTag';
import SEOHead from '@/components/SEOHead';
import useDnsCheck from '@/hooks/useDnsCheck';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Card, Col, Descriptions, Divider, Row, Tag, Typography } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

const BimiCheckerPage = () => {
  const { data, isLoading, submitCheck } = useDnsCheck();
  const router = useRouter();
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Check if there's a domain query parameter and initiate a DNS check
    if (router.query.domain) {
      submitCheck(router.query.domain);
    }
  }, [router.query, submitCheck]);

  // Debug certificate info when data changes
  useEffect(() => {
    if (data && data.certificateInfo) {
      console.log('Certificate Info in component:', data.certificateInfo);
    }
  }, [data]);

  const getBimiUrl = (bimiRecords) => {
    if (!bimiRecords || !Array.isArray(bimiRecords) || bimiRecords.length === 0) {
      return '';
    }
    const bimiRegex = /v=BIMI1;\s*l=([^;]+)(?:;|$)/;
    for (const record of bimiRecords) {
      const match = record.join('').match(bimiRegex);
      if (match) {
        return match[1];
      }
    }
    return '';
  };

  const bimiUrl = data && data.records.BIMI ? getBimiUrl(data.records.BIMI) : '';
  const hasCertInfo = data && data.certificateInfo && Object.keys(data.certificateInfo).length > 0;
  const logoMismatch = data?.certificateInfo?.logoValidation?.valid === false;

  // Helper function to display certificate value or fallback
  const displayCertValue = (value, fallback = 'Not Available') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    // Ensure any backslashes are removed from the displayed value
    return typeof value === 'string' ? value.replace(/\\/g, '') : value;
  };

  return (
    <div>
      <SEOHead />

      <section
        className="products section hero v4"
        style={{ paddingTop: '132px', paddingBottom: '0' }}
      >
        <div className="container-default w-container">
          <div className="z-index-1">
            <div className="mg-bottom-24px">
              <div className="inner-container center">
                <div className="inner-container _861px---tablet center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        <p>ARE YOU READY FOR VMC?</p>
                      </div>
                    </div>
                    <h1 className="mg-bottom-16px">BIMI Record Checker</h1>
                    <div className="inner-container _745px center">
                      <p className="color-neutral-600 mg-bottom-0">
                        Enter your domain and we'll check if you are BIMI & DMARC compliant. If you
                        already have a BIMI logo, we'll show you what it will look like after you
                        buy a VMC.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="certificate_request_app" className="bg-neutral-100" style={{ paddingTop: '0' }}>
        <div className="container-default w-container">
          <div className="mg-bottom-16px">
            <div className="inner-container">
              <Row gutter={[24, 24]}>
                <Col xs={24}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <DnsCheckForm onSubmit={submitCheck} isLoading={isLoading} />
                    <FullscreenSpinner isLoading={isLoading} tip="Checking DNS records..." />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          <div className="mg-bottom-20px">
            <div className="inner-container">
              <Row gutter={[24, 24]}>
                <Col xs={24}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    {data && (
                      <h2>
                        Results for{' '}
                        <Link href={`https://${data.domain}`} target="_blank">
                          {data.domain}
                        </Link>
                      </h2>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          <div className="mg-bottom-80px">
            <div className="inner-container">
              <Row gutter={[24, 24]}>
                <Col xs={24} md={16}>
                  {data && <DnsResults data={data} />}
                </Col>

                <Col xs={24} md={8}>
                  {data && (
                    <>
                      <Card
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          marginBottom: '20px',
                        }}
                      >
                        <h3>
                          BIMI Logo:
                          {data &&
                            (bimiUrl && !logoMismatch ? (
                              <CheckCircleOutlined style={{ color: 'green', marginLeft: '10px' }} />
                            ) : (
                              <CloseCircleOutlined style={{ color: 'red', marginLeft: '10px' }} />
                            ))}
                        </h3>
                        {logoMismatch && (
                          <div style={{ marginBottom: '12px' }}>
                            <LogoIntegrityTag
                              logoValidation={data.certificateInfo.logoValidation}
                            />
                          </div>
                        )}
                        {data &&
                          (bimiUrl ? (
                            <div>
                              {!imageError ? (
                                <img
                                  src={bimiUrl}
                                  alt="BIMI Logo"
                                  style={{ maxWidth: '100%', height: 'auto' }}
                                  onError={() => setImageError(true)}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    lineHeight: '24px',
                                  }}
                                >
                                  <WarningOutlined
                                    style={{ color: '#d4a017', fontSize: '164px' }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              <div
                                style={{ width: '100%', textAlign: 'center', lineHeight: '24px' }}
                              >
                                <WarningOutlined style={{ color: '#d4a017', fontSize: '164px' }} />
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  height: '100%',
                                }}
                              >
                                <Typography.Text>No BIMI logo found!</Typography.Text>
                              </div>

                              <div
                                className="buttons-row mg-top-16px"
                                style={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Link
                                  href="/products/digicert-verified-mark-certificate"
                                  className="btn-primary w-button width-100"
                                >
                                  Get BIMI Compliant Now
                                  <span className="line-rounded-icon link-icon-right"></span>
                                </Link>
                              </div>
                            </>
                          ))}
                      </Card>

                      {hasCertInfo && (
                        <Card
                          title={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <SafetyCertificateOutlined
                                style={{ fontSize: '18px', marginRight: '8px', color: '#52c41a' }}
                              />
                              <span>
                                {data.certificateInfo.certificateType === 'CMC' ||
                                (data.certificateInfo.subject &&
                                  data.certificateInfo.subject.includes('Prior Use Mark'))
                                  ? 'CMC'
                                  : 'VMC'}{' '}
                                Certificate Details
                              </span>
                            </div>
                          }
                          style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          }}
                        >
                          <Descriptions
                            bordered
                            size="small"
                            column={1}
                            styles={{ label: { fontWeight: 'bold' } }}
                          >
                            <Descriptions.Item label="Organization">
                              {displayCertValue(data.certificateInfo.organization)}
                            </Descriptions.Item>

                            <Descriptions.Item label="Certificate Type">
                              {data.certificateInfo.subject &&
                              data.certificateInfo.subject.includes('Prior Use Mark')
                                ? 'CMC'
                                : displayCertValue(data.certificateInfo.certificateType, 'VMC')}
                            </Descriptions.Item>

                            {data.certificateInfo.markType && (
                              <Descriptions.Item label="Mark Type">
                                {displayCertValue(data.certificateInfo.markType)}
                              </Descriptions.Item>
                            )}

                            <Descriptions.Item label="Valid From">
                              {displayCertValue(data.certificateInfo.validFrom)}
                            </Descriptions.Item>

                            <Descriptions.Item label="Valid To">
                              {displayCertValue(data.certificateInfo.validTo)}
                            </Descriptions.Item>

                            <Descriptions.Item label="Days Remaining">
                              {data.certificateInfo.daysLeft !== undefined ? (
                                <Tag color={data.certificateInfo.daysLeft > 30 ? 'green' : 'red'}>
                                  {data.certificateInfo.daysLeft} days
                                </Tag>
                              ) : (
                                'Not Available'
                              )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Issuer">
                              {displayCertValue(
                                data.certificateInfo.caOrganization || data.certificateInfo.issuer
                              )}
                            </Descriptions.Item>

                            {data.certificateInfo.logoValidation && (
                              <Descriptions.Item label="Logo Integrity">
                                <LogoIntegrityTag
                                  logoValidation={data.certificateInfo.logoValidation}
                                />
                              </Descriptions.Item>
                            )}

                            {data.certificateInfo.trademarkRegistration && (
                              <Descriptions.Item label="Trademark Registration">
                                {data.certificateInfo.trademarkRegistration}
                              </Descriptions.Item>
                            )}

                            {data.certificateInfo.serialNumber && (
                              <Descriptions.Item label="Serial Number">
                                {data.certificateInfo.serialNumber}
                              </Descriptions.Item>
                            )}
                          </Descriptions>

                          <Divider />

                          <div>
                            <a
                              href={data.certificateInfo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-primary w-button width-100"
                              style={{ textAlign: 'center', display: 'block' }}
                            >
                              View Full Certificate
                            </a>
                          </div>
                        </Card>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BimiCheckerPage;
